import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types';
import { Autocomplete } from '@material-ui/lab';
import { TextField, CircularProgress } from '@material-ui/core';
import notice from "./notice.js"

export const Search = ({ setterFunction, field, value, label, error, searchFunction, slug, disabled=false, required=false  }) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [search, setSearch] = useState("")
  const loading = open && options.length === 0;

  useEffect(() => {
    if (slug) {
      if (open) searchFunction(setOptions, slug, search)
    } else notice("No school was selected")
  }, [open, search, searchFunction, slug])

  useEffect(() => {
    if (!open) setOptions([])

  }, [open])

  return (
    <Autocomplete
      disabled={disabled}
      id={field + "_search_input"}
      fullWidth
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      value={value}
      inputValue={search}
      onChange={(e, newValue) => setterFunction(field, newValue)}
      onInputChange={(e, newInputValue) => setSearch(newInputValue)}
      getOptionSelected={(option, value) => {
        // Defensive: MUI calls with two option objects OR with the
        // raw value when the form's current value isn't in `options`.
        // Also fixes a typo — the third clause was
        // `options.user.first_name` (the options array, not the
        // current option) which was always undefined and crashed
        // the second time onwards.
        //
        // Each clause now also verifies the comparison field is
        // present on BOTH sides before equating — without the
        // null-guards `undefined === undefined` would falsely match
        // any option/value pair that simply lacks the same field.
        // Real risk because this component is reused for teams
        // (.name), students (.first_name), and student-wrappers
        // (.user.first_name); a teams-shaped value compared against
        // a students-shaped option would have wrongly returned
        // true on the .name branch (both undefined).
        if (!option || !value || typeof option !== "object" || typeof value !== "object") {
          return false;
        }
        return (
          (option.name != null && value.name != null && option.name === value.name) ||
          (option.first_name != null && value.first_name != null && option.first_name === value.first_name) ||
          (option.user && value.user &&
            option.user.first_name != null && value.user.first_name != null &&
            option.user.first_name === value.user.first_name)
        );
      }}
      getOptionLabel={(option) => {
        // Same call-shape guard as TimeZoneSelector (admin PR #545).
        // Old impl returned "" only when option was falsy entirely;
        // but Autocomplete also calls this with a raw string id
        // before async-loaded options arrive, and `option.user` then
        // crashed. Walk the shapes explicitly.
        if (!option || typeof option !== "object") return "";
        if (option.name) return option.name;
        if (option.user && option.user.first_name) {
          return `${option.user.first_name} ${option.user.last_name || ""}`.trim();
        }
        if (option.first_name) {
          return `${option.first_name} ${option.last_name || ""}`.trim();
        }
        return "";
      }}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          variant="outlined"
          required={required}
          //error={Boolean(error)}
          //helperText={error}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress size={20} />}
                {params.InputProps.endAdornment}
              </>
            )
          }}
        />
      )}
    />
  )
}

Search.propTypes = {
  setterFunction: PropTypes.func.isRequired,
  // Required: passed to setterFunction(field, newValue) on every
  // selection. The component is unusable without it.
  field: PropTypes.string.isRequired,
  // Selected value: option object from the searchFunction result,
  // or a string / number id, or empty. Different callsites pass
  // different shapes.
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
  label: PropTypes.string,
  error: PropTypes.string,
  // Required: (setterFunction, slug, searchText) => void —
  // fetches and pushes results into setterFunction. Called from
  // the useEffect that drives the dropdown's options.
  searchFunction: PropTypes.func.isRequired,
  slug: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
}
